import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { 
  FiCamera, FiExternalLink, FiLock, FiCopy, FiCheck, FiClock 
} from 'react-icons/fi';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PhotoGalleryView({ reservationId }) {
  const [loading, setLoading] = useState(true);
  const [gallery, setGallery] = useState(null);
  const [copied, setCopied] = useState(false);

  const loadGallery = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/reservations/${reservationId}/gallery`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.exists && data.gallery.enabled) {
          setGallery(data.gallery);
        }
      }
    } catch (error) {
      console.error('Error loading gallery:', error);
    } finally {
      setLoading(false);
    }
  }, [reservationId]);

  useEffect(() => {
    loadGallery();
  }, [loadGallery]);

  const copyPassword = () => {
    if (gallery?.password) {
      navigator.clipboard.writeText(gallery.password);
      setCopied(true);
      toast.success('Mot de passe copié !');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <Card className="border-slate-200">
        <CardContent className="py-6">
          <div className="flex items-center justify-center">
            <FiClock className="h-5 w-5 animate-spin text-slate-400" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!gallery) {
    return (
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <FiCamera className="h-5 w-5" />
            Photos de l'événement
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4">
            <FiClock className="h-10 w-10 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-600 font-medium">Photos en préparation</p>
            <p className="text-sm text-slate-500 mt-1">
              Les photos de votre événement seront bientôt disponibles ici.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-slate-200 overflow-hidden" data-testid="photo-gallery-view">
      <CardHeader className="bg-gradient-to-r from-purple-500 to-pink-500 text-white pb-4">
        <CardTitle className="flex items-center gap-2 text-base">
          <FiCamera className="h-5 w-5" />
          Vos photos sont disponibles !
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 pt-4">
        <p className="text-sm text-slate-600">
          Les photos de votre événement sont prêtes. Cliquez sur le bouton ci-dessous pour accéder à la galerie.
        </p>

        {/* Password info */}
        {gallery.password && (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FiLock className="h-4 w-4 text-amber-600" />
                <span className="text-sm font-medium text-amber-800">
                  Mot de passe requis
                </span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={copyPassword}
                className="text-amber-700 hover:text-amber-800 hover:bg-amber-100"
              >
                {copied ? (
                  <FiCheck className="h-4 w-4 mr-1" />
                ) : (
                  <FiCopy className="h-4 w-4 mr-1" />
                )}
                {copied ? 'Copié !' : 'Copier'}
              </Button>
            </div>
            <p className="text-sm text-amber-700 mt-2 font-mono bg-amber-100 px-2 py-1 rounded">
              {gallery.password}
            </p>
          </div>
        )}

        {/* Access button */}
        <Button
          asChild
          className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
          data-testid="access-gallery-btn"
        >
          <a href={gallery.url} target="_blank" rel="noopener noreferrer">
            <FiExternalLink className="mr-2 h-4 w-4" />
            Accéder à mes photos
          </a>
        </Button>

        <p className="text-xs text-slate-500 text-center">
          N'hésitez pas à télécharger et partager ces souvenirs !
        </p>
      </CardContent>
    </Card>
  );
}
